import React, { useState } from 'react';
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { useFormularioAreas } from '@/hooks/useFormularioAreas';
import { FormularioArea } from '@/types/formulario';
import { Plus, GripVertical, Pencil, Trash2, X } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

interface FormularioAreasManagerProps {
  formularioId: string;
  onClose: () => void;
}

interface AreaItemProps {
  area: FormularioArea;
  onEdit: (area: FormularioArea) => void;
  onDelete: (areaId: string) => void;
}

const AreaItem = ({ area, onEdit, onDelete }: AreaItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: area.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 p-3 bg-card border rounded-lg hover:bg-accent/50 transition-colors"
    >
      <div {...attributes} {...listeners} className="cursor-grab hover:text-primary">
        <GripVertical className="h-5 w-5" />
      </div>
      
      <div className="flex-1">
        <h4 className="font-medium">{area.titulo}</h4>
        {area.descricao && (
          <p className="text-sm text-muted-foreground">{area.descricao}</p>
        )}
      </div>

      <div className="flex gap-1">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onEdit(area)}
        >
          <Pencil className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onDelete(area.id)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export const FormularioAreasManager = ({ formularioId, onClose }: FormularioAreasManagerProps) => {
  const { areas, addArea, updateArea, deleteArea, reorderAreas } = useFormularioAreas(formularioId);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingArea, setEditingArea] = useState<FormularioArea | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [areaToDelete, setAreaToDelete] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    titulo: '',
    descricao: '',
    expansivel: true,
    expandida_por_padrao: true,
  });

  const handleOpenDialog = (area?: FormularioArea) => {
    if (area) {
      setEditingArea(area);
      setFormData({
        titulo: area.titulo,
        descricao: area.descricao || '',
        expansivel: area.expansivel,
        expandida_por_padrao: area.expandida_por_padrao,
      });
    } else {
      setEditingArea(null);
      setFormData({
        titulo: '',
        descricao: '',
        expansivel: true,
        expandida_por_padrao: true,
      });
    }
    setDialogOpen(true);
  };

  const handleSave = () => {
    if (!formData.titulo.trim()) {
      return;
    }

    if (editingArea) {
      updateArea({
        id: editingArea.id,
        ...formData,
      });
    } else {
      addArea({
        formulario_id: formularioId,
        ordem: areas.length,
        ativo: true,
        ...formData,
      });
    }

    setDialogOpen(false);
  };

  const handleDeleteConfirm = () => {
    if (areaToDelete) {
      deleteArea(areaToDelete);
      setDeleteDialogOpen(false);
      setAreaToDelete(null);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = areas.findIndex((a) => a.id === active.id);
      const newIndex = areas.findIndex((a) => a.id === over.id);

      const reordered = arrayMove(areas, oldIndex, newIndex);
      const updates = reordered.map((area, index) => ({
        id: area.id,
        ordem: index,
      }));

      reorderAreas(updates);
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="border-b p-4 flex items-center justify-between bg-background">
        <div>
          <h2 className="text-2xl font-bold">Gerenciar Áreas</h2>
          <p className="text-sm text-muted-foreground">
            Organize os campos do formulário em seções temáticas
          </p>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="w-full space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">
              Áreas ({areas.length})
            </h3>
            <Button onClick={() => handleOpenDialog()}>
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Área
            </Button>
          </div>

          {areas.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-muted-foreground mb-4">
                Nenhuma área criada ainda. Adicione áreas para organizar seus campos.
              </p>
              <Button onClick={() => handleOpenDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                Criar Primeira Área
              </Button>
            </Card>
          ) : (
            <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={areas.map(a => a.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {areas.map((area) => (
                    <AreaItem
                      key={area.id}
                      area={area}
                      onEdit={handleOpenDialog}
                      onDelete={(id) => {
                        setAreaToDelete(id);
                        setDeleteDialogOpen(true);
                      }}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>

      {/* Dialog Criar/Editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingArea ? 'Editar Área' : 'Nova Área'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="titulo">Título *</Label>
              <Input
                id="titulo"
                value={formData.titulo}
                onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                placeholder="Ex: Informações Básicas"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição (opcional)</Label>
              <Textarea
                id="descricao"
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                placeholder="Descreva o propósito desta área..."
                rows={3}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="expansivel">Seção expansível</Label>
              <Switch
                id="expansivel"
                checked={formData.expansivel}
                onCheckedChange={(checked) => setFormData({ ...formData, expansivel: checked })}
              />
            </div>

            {formData.expansivel && (
              <div className="flex items-center justify-between">
                <Label htmlFor="expandida">Expandida por padrão</Label>
                <Switch
                  id="expandida"
                  checked={formData.expandida_por_padrao}
                  onCheckedChange={(checked) => setFormData({ ...formData, expandida_por_padrao: checked })}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={!formData.titulo.trim()}>
              {editingArea ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Confirmar Exclusão */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover Área</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover esta área? Os campos dela serão movidos para "Sem área".
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
